import type { Modo } from "../game/modo";
import type { SaveMeta } from "../game/types";
import { tempoRelativo, useT, type Traduzir } from "../i18n";
import type { Carreira } from "../online/carreira";
import { LICENCA_DESLIGADA, type Liberacao } from "../online/licenca";
import { useSessao } from "../online/sessao";
import { diasDeTeste, useLicenca } from "../online/useLicenca";
import { PortaDeEntrada } from "./PortaDeEntrada";
import { FundoDeAbertura } from "../ui/FundoDeAbertura";

/** A mesma origem que `online/sessao.tsx` e o relatório de erros já usam. */
const VERSAO = import.meta.env.VITE_APP_VERSION ?? "0.1.0";

/** O que a tela precisa para a carreira da conta. */
export interface PainelOnline {
  /** A carreira que está no servidor, ou `null` se a conta ainda não tem uma. */
  carreira: Carreira | null;
  /** Ainda perguntando ao servidor. Evita piscar "sem carreira" antes da resposta. */
  carregando: boolean;
  aoComecar: () => void;
  aoContinuar: () => void;
  aoExportar: () => void;
  aoApagar: () => void;
  /** Exportar só faz sentido onde existe modo 1 Jogador para abrir o arquivo. */
  podeExportar: boolean;
}

/** O que a tela precisa para o modo de um jogador só. */
export interface PainelUmJogador {
  saves: SaveMeta[];
  onNew: () => void;
  /** Abre uma carreira com um banco de dados vindo de arquivo. */
  onImportPack: (file: File) => void;
  /** Abre o editor de banco de dados. */
  onEditor: () => void;
  onContinue: (saveId: string) => void;
  onDelete: (saveId: string) => void;
}

/**
 * Tela de abertura.
 *
 * O emblema é o título: o nome do jogo está escrito dentro dele, então repetir
 * "Legacy Manager" em letra grande logo abaixo seria dizer a mesma coisa duas
 * vezes. O fundo é a foto do estádio numa versão leve, gerada por `pnpm arte`.
 *
 * **O que esta tela oferece depende da plataforma.** No aplicativo instalado
 * ela começa perguntando entre 1 Jogador e Online Multiplayer. No navegador não
 * há o que perguntar: só existe o online, porque a aba pode fechar, o
 * armazenamento pode ser limpo pelo próprio navegador e não há arquivo para
 * onde exportar.
 */
export function Home({
  modo,
  modos,
  aoEscolherModo,
  online,
  umJogador,
  busy,
  aoComprar,
}: {
  /** `null` no aplicativo enquanto ninguém escolheu. */
  modo: Modo | null;
  modos: Modo[];
  aoEscolherModo: (modo: Modo | null) => void;
  online: PainelOnline;
  umJogador: PainelUmJogador;
  busy: boolean;
  /** Abre a loja, para quem está no teste ou já passou dele. */
  aoComprar: () => void;
}) {
  const t = useT();

  return (
    <div
      className="fundo-estadio"
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px var(--gutter)",
      }}
    >
      <FundoDeAbertura />
      <div style={{ width: "min(880px, 100%)", textAlign: "center" }}>
        <div
          style={{
            width: "clamp(150px, 26vw, 232px)",
            height: "clamp(150px, 26vw, 232px)",
            margin: "0 auto",
          }}
        >
          {/* Sem texto ao lado, é o emblema que carrega o nome para quem usa
              leitor de tela. Por isso o `alt` não é vazio. */}
          <img
            className="emblema"
            src="/arte/emblema.webp"
            alt={t("comum.marca")}
            width={360}
            height={360}
          />
        </div>

        <p
          className="label"
          style={{ marginTop: 14, fontSize: 12, letterSpacing: "0.22em", color: "var(--text-muted)" }}
        >
          {t("home.lema")}
        </p>

        {modo === null ? (
          <EscolhaDeModo t={t} modos={modos} aoEscolher={aoEscolherModo} busy={busy} />
        ) : modo === "online" ? (
          <PainelDaConta t={t} painel={online} busy={busy} aoComprar={aoComprar} />
        ) : (
          <PortaoDoUmJogador t={t} painel={umJogador} busy={busy} aoComprar={aoComprar} />
        )}

        {/* Voltar à escolha só existe onde há escolha: no aplicativo. */}
        {modo !== null && modos.length > 1 && (
          <button
            className="ghost"
            disabled={busy}
            onClick={() => aoEscolherModo(null)}
            style={{ marginTop: 18, minHeight: 32, fontSize: 12.5 }}
          >
            {t("home.modo.trocar")}
          </button>
        )}

        <p className="label-muted" style={{ marginTop: 30 }}>
          {t("home.versao", { versao: VERSAO })}
        </p>
      </div>
    </div>
  );
}

/**
 * A pergunta do aplicativo: sozinho ou com gente.
 *
 * Só os dois nomes. O que cada modo faz está na documentação e na página de
 * suporte; tela de abertura de programa não explica os próprios botões.
 */
function EscolhaDeModo({
  t,
  modos,
  aoEscolher,
  busy,
}: {
  t: Traduzir;
  modos: Modo[];
  aoEscolher: (modo: Modo) => void;
  busy: boolean;
}) {
  return (
    <div
      className="grid"
      style={{
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: 14,
        marginTop: 28,
        textAlign: "left",
      }}
    >
      {modos.map((modo) => (
        <button
          key={modo}
          className={modo === "umJogador" ? "primary" : "ghost"}
          disabled={busy}
          onClick={() => aoEscolher(modo)}
          style={{ minHeight: 60, fontSize: 15, fontWeight: 700 }}
        >
          {modo === "umJogador" ? t("home.modo.umJogador") : t("home.modo.online")}
        </button>
      ))}
    </div>
  );
}

/**
 * A carreira da conta.
 *
 * Três estados, e cada um tem uma única coisa a oferecer: sem sessão, entrar;
 * com sessão e sem carreira, começar; com carreira, continuar. É a tela mais
 * simples do jogo de propósito, porque é a que aparece antes de tudo.
 */
function PainelDaConta({
  t,
  painel,
  busy,
  aoComprar,
}: {
  t: Traduzir;
  painel: PainelOnline;
  busy: boolean;
  aoComprar: () => void;
}) {
  const sessao = useSessao();
  const licenca = useLicenca();
  const bloqueada = licencaBloqueada(licenca.liberacao);
  const jogar = (acao: () => void) => () => {
    void licenca.garantir().then((pode) => {
      if (pode) acao();
    });
  };

  /*
   * Enquanto a sessão está sendo resolvida, não há o que oferecer.
   *
   * É neste intervalo que a Steam responde, no aplicativo instalado. Mostrar
   * "Entrar na conta" aqui faria o botão aparecer e sumir sozinho na cara de
   * quem abriu o jogo pela Steam, que nunca vai precisar dele.
   */
  if (sessao.estado === "carregando") return null;

  if (!sessao.conta) {
    /*
     * Entrar acontece aqui, e não numa tela à parte.
     *
     * Havia um botão "Entrar na conta" que levava para a ficha da conta, onde
     * o formulário estava. Eram dois passos para a única coisa que essa tela
     * tem a oferecer a quem chegou: o emblema já está logo acima, e o que falta
     * é e-mail e senha.
     *
     * A largura é curta porque campo de texto largo demais atrapalha: a coluna
     * da abertura tem 880 e um formulário de duas linhas esticado nela vira uma
     * faixa. O texto volta a alinhar à esquerda, que é como formulário se lê.
     */
    return (
      <div style={{ maxWidth: 360, margin: "24px auto 0", textAlign: "left" }}>
        <PortaDeEntrada />
      </div>
    );
  }

  if (painel.carregando) {
    return (
      <p className="muted" style={{ marginTop: 28 }}>
        {t("home.online.procurando")}
      </p>
    );
  }

  if (!painel.carreira) {
    return (
      <div style={{ marginTop: 28 }}>
        <SituacaoDaLicenca t={t} aoComprar={aoComprar} />
        {!bloqueada && (
          <button
            className="primary"
            disabled={busy}
            onClick={jogar(painel.aoComecar)}
            style={{ minWidth: 260, minHeight: 46, fontSize: 15 }}
          >
            {t("home.online.comecar")}
          </button>
        )}
        <ErroDaLicenca />
      </div>
    );
  }

  const carreira = painel.carreira;

  return (
    <div style={{ marginTop: 28 }}>
      <SituacaoDaLicenca t={t} aoComprar={aoComprar} />
      {!bloqueada && (
        <button
          className="primary"
          disabled={busy}
          onClick={jogar(painel.aoContinuar)}
          style={{ minWidth: 300, minHeight: 46, fontSize: 15 }}
        >
          {t("home.online.continuar", { clube: carreira.clubName })}
        </button>
      )}
      <ErroDaLicenca />

      <p className="muted" style={{ marginTop: 10, fontSize: 12.5 }}>
        {t("home.online.resumo", {
          treinador: carreira.managerName,
          temporada: carreira.season,
          rodada: carreira.round,
          quando: tempoRelativo(Date.parse(carreira.updatedAt)),
        })}
      </p>

      <div className="row" style={{ gap: 10, justifyContent: "center", marginTop: 16 }}>
        {painel.podeExportar && (
          <button
            className="ghost"
            disabled={busy}
            onClick={painel.aoExportar}
            style={{ minHeight: 34, fontSize: 12.5 }}
          >
            {t("home.online.exportar")}
          </button>
        )}
        <button
          className="ghost"
          disabled={busy}
          onClick={painel.aoApagar}
          style={{ minHeight: 34, fontSize: 12.5, color: "var(--danger)" }}
        >
          {t("home.online.apagar")}
        </button>
      </div>
    </div>
  );
}

/**
 * O modo de um jogador só, que existe apenas no aplicativo instalado.
 *
 * A carreira daqui é deste aparelho: não sobe, não entra em ranqueado e não
 * volta para a conta.
 */
function PainelLocal({
  t,
  painel,
  busy,
  podeJogar,
}: {
  t: Traduzir;
  painel: PainelUmJogador;
  busy: boolean;
  /** Falso depois do teste sem compra: as carreiras aparecem, mas não abrem. */
  podeJogar: boolean;
}) {
  const ultima = painel.saves[0];

  return (
    <div style={{ marginTop: 28 }}>
      <div className="row" style={{ justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
        {ultima && podeJogar && (
          <button
            className="primary"
            style={{ minWidth: 260, minHeight: 46, fontSize: 15 }}
            disabled={busy}
            onClick={() => painel.onContinue(ultima.id)}
          >
            {t("home.continuarCom", { clube: ultima.clubName })}
          </button>
        )}
        {podeJogar && (
          <button
            className={ultima ? "ghost" : "primary"}
            style={{ minWidth: 220, minHeight: 46, fontSize: 15 }}
            disabled={busy}
            onClick={painel.onNew}
          >
            {t("home.novaCarreira")}
          </button>
        )}
      </div>

      {/*
        Banco de dados próprio. É o que mantém um jogo destes vivo por anos:
        times de verdade, nomes de verdade, o campeonato da sua cidade. O
        arquivo é um JSON legível, então dá para corrigir um nome no bloco de
        notas sem precisar de editor nenhum.
      */}
      <div className="row" style={{ gap: 10, justifyContent: "center", marginTop: 14 }}>
        <label
          className="ghost"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 34,
            padding: "0 14px",
            borderRadius: 8,
            cursor: busy || !podeJogar ? "default" : "pointer",
            fontSize: 12.5,
            opacity: busy || !podeJogar ? 0.5 : 1,
          }}
        >
          {t("home.bancoProprio")}
          <input
            type="file"
            accept=".json,application/json"
            disabled={busy || !podeJogar}
            style={{ display: "none" }}
            onChange={(event) => {
              const arquivo = event.target.files?.[0];
              event.target.value = "";
              if (arquivo) painel.onImportPack(arquivo);
            }}
          />
        </label>
        <button
          className="ghost"
          disabled={busy}
          onClick={painel.onEditor}
          style={{ minHeight: 34, fontSize: 12.5 }}
        >
          {t("home.editorDeBanco")}
        </button>
      </div>

      {painel.saves.length > 0 && (
        <div className="card" style={{ marginTop: 26, textAlign: "left", padding: 0 }}>
          <header style={{ padding: "10px 14px", borderBottom: "1px solid var(--border)" }}>
            <span className="label">{t("home.carreirasSalvas")}</span>
          </header>
          <div style={{ padding: 6 }}>
            {painel.saves.map((save) => (
              <div key={save.id} className="row" style={{ gap: 10, padding: "8px 8px" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{save.clubName}</div>
                  <div className="muted" style={{ fontSize: 11.5 }}>
                    {t("home.resumoDoSave", {
                      treinador: save.managerName,
                      temporada: save.season,
                      rodada: save.round,
                      total: save.totalRounds,
                      quando: tempoRelativo(save.updatedAt),
                    })}
                  </div>
                </div>
                <button
                  className="ghost"
                  disabled={busy || !podeJogar}
                  onClick={() => painel.onContinue(save.id)}
                >
                  {t("comum.continuar")}
                </button>
                <button
                  className="ghost"
                  disabled={busy}
                  onClick={() => painel.onDelete(save.id)}
                  style={{ color: "var(--danger)" }}
                >
                  {t("home.apagar")}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Passou do teste sem compra, ou o aparelho já gastou o teste em outra conta. */
function licencaBloqueada(liberacao: Liberacao): boolean {
  if (LICENCA_DESLIGADA || liberacao.liberado) return false;
  return liberacao.status === "expired" || liberacao.status === "trial_used";
}

/**
 * O portão do modo 1 Jogador.
 *
 * O modo roda sem internet, mas o jogo é comprado: sem um bilhete de licença
 * válido neste aparelho, a primeira coisa é entrar na conta (uma vez só; o
 * login fica guardado no cofre do sistema). Com a conta, cada botão que abre
 * carreira passa por `garantir()`, que começa o teste na primeira vez.
 */
function PortaoDoUmJogador({
  t,
  painel,
  busy,
  aoComprar,
}: {
  t: Traduzir;
  painel: PainelUmJogador;
  busy: boolean;
  aoComprar: () => void;
}) {
  const sessao = useSessao();
  const licenca = useLicenca();

  if (LICENCA_DESLIGADA) return <PainelLocal t={t} painel={painel} busy={busy} podeJogar />;
  if (!licenca.pronta) return null;

  if (!licenca.liberacao.liberado) {
    if (sessao.estado === "carregando") return null;
    if (!sessao.conta) {
      return (
        <div style={{ maxWidth: 360, margin: "24px auto 0", textAlign: "left" }}>
          <PortaDeEntrada />
        </div>
      );
    }
  }

  const jogar = (acao: () => void) => {
    void licenca.garantir().then((pode) => {
      if (pode) acao();
    });
  };
  const comLicenca: PainelUmJogador = {
    ...painel,
    onNew: () => jogar(painel.onNew),
    onContinue: (saveId) => jogar(() => painel.onContinue(saveId)),
    onImportPack: (arquivo) => jogar(() => painel.onImportPack(arquivo)),
  };

  return (
    <div style={{ marginTop: 28 }}>
      <SituacaoDaLicenca t={t} aoComprar={aoComprar} />
      <PainelLocal t={t} painel={comLicenca} busy={busy} podeJogar={!licencaBloqueada(licenca.liberacao)} />
      <ErroDaLicenca />
    </div>
  );
}

/**
 * Onde a licença está: os dias de teste que faltam, ou o fim dele.
 *
 * Só rótulo e botão. Comprada, não aparece nada: quem pagou não precisa ser
 * lembrado disso a cada abertura.
 */
function SituacaoDaLicenca({ t, aoComprar }: { t: Traduzir; aoComprar: () => void }) {
  const { liberacao } = useLicenca();
  if (LICENCA_DESLIGADA) return null;

  const bloqueada = licencaBloqueada(liberacao);
  let rotulo: string | null = null;
  if (bloqueada) {
    rotulo = liberacao.status === "trial_used" ? t("home.licenca.testeUsado") : t("home.licenca.encerrado");
  } else if (liberacao.liberado && liberacao.status === "trial") {
    const dias = diasDeTeste(liberacao.trialEndsAt);
    rotulo = dias <= 1 ? t("home.licenca.ultimoDia") : t("home.licenca.teste", { dias });
  } else if (!liberacao.liberado && (liberacao.status === "trial" || liberacao.status === "licensed")) {
    rotulo = t("home.licenca.relogio");
  }
  if (!rotulo) return null;

  return (
    <div
      className="row"
      style={{ gap: 12, justifyContent: "center", alignItems: "center", marginBottom: 18, flexWrap: "wrap" }}
    >
      <span className="label" style={{ color: bloqueada ? "var(--danger)" : "var(--text-muted)" }}>
        {rotulo}
      </span>
      <button
        className={bloqueada ? "primary" : "ghost"}
        onClick={aoComprar}
        style={{ minHeight: bloqueada ? 46 : 32, fontSize: bloqueada ? 15 : 12.5 }}
      >
        {t("home.licenca.comprar")}
      </button>
    </div>
  );
}

function ErroDaLicenca() {
  const { erro } = useLicenca();
  if (!erro) return null;
  return (
    <p role="alert" style={{ marginTop: 12, fontSize: 12.5, color: "var(--danger)" }}>
      {erro}
    </p>
  );
}
